import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { 
  Clock, AlertTriangle, CheckCircle2, XCircle, 
  ChevronLeft, ChevronRight, Grid, List, Flag
} from "lucide-react";

// Canadian maple leaf SVG component - Realistic Canadian flag maple leaf  
const MapleLeaf = ({ className }) => (
  <svg className={className} viewBox="0 0 512 512" fill="currentColor">
    <path d="M256 32l16 80 64-48-32 80h96l-64 64 80 32-64 32 48 128-96-64-16 144-32-112-32 112-16-144-96 64 48-128-64-32 80-32-64-64h96l-32-80 64 48z"/>
  </svg>
);

const RealTestUI = ({ auth }) => {
  const { t } = useLanguage();
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState(location.state?.questions || []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [reviewLater, setReviewLater] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState((location.state?.timeLimit || 45) * 60);
  const [testCompleted, setTestCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  
  const isOfficialMock = location.state?.isOfficialMock || false;
  const isSimulation = location.state?.isSimulation || false;
  const passScore = location.state?.passScore || 75;
  const testTitle = location.state?.title || "Online citizenship test";

  // Generate mock Application Number and UCI
  const applicationNumber = useMemo(() => "M" + Math.random().toString().slice(2, 12), []);
  const uciNumber = useMemo(() => Math.random().toString().slice(2, 12), []);

  // Timer with auto-submit
  useEffect(() => {
    if (testCompleted || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          toast.warning("Time's up! Submitting your test...");
          handleSubmit();
          return 0;
        }
        // Show warning at 5 minutes
        if (prev === 300) {
          toast.warning("5 minutes remaining!");
        }
        // Show warning at 1 minute
        if (prev === 60) {
          toast.warning("1 minute remaining!");
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testCompleted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const toggleReviewLater = (questionId) => {
    setReviewLater(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setShowSubmitConfirm(false);

    // Convert answers array to dictionary format expected by backend
    const answersDict = {};
    questions.forEach((q) => {
      if (answers[q.question_id] !== undefined && answers[q.question_id] !== null) {
        answersDict[q.question_id] = answers[q.question_id];
      }
    });

    const submission = {
      answers: answersDict,
      time_taken: (location.state?.timeLimit || 45) * 60 - timeLeft,
      mock_test_id: location.state?.mockTestId || null,
      simulation_id: location.state?.simulationId || null
    };

    try {
      const storedToken = localStorage.getItem("token") || auth.token;
      const response = await axios.post(`${API}/tests/${testId}/submit`, submission, {
        withCredentials: true,
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
      });
      setResults(response.data);
      setTestCompleted(true);
      
      if (response.data.passed) {
        toast.success(`Congratulations! You passed with ${response.data.score}%`);
      } else {
        toast.info(`You scored ${response.data.score}%. Keep practicing!`);
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit test. Please try again.");
      setSubmitting(false);
    }
  }, [answers, questions, testId, timeLeft, submitting, auth.token, location.state]);

  const goToQuestion = (index) => {
    setCurrentIndex(index);
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const reviewCount = reviewLater.size;
  const notAnsweredCount = questions.length - answeredCount;

  // Check if question is answered
  const getQuestionStatus = (index) => {
    const q = questions[index];
    const isAnswered = !!answers[q.question_id];
    const isReview = reviewLater.has(q.question_id);
    return { isAnswered, isReview };
  };

  if (!questions.length) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="font-bold text-xl text-slate-900 mb-2">Test Not Found</h2>
            <p className="text-slate-600 mb-6">
              The test data is not available. Please start a new test.
            </p>
            <Button onClick={() => navigate("/simulations")} className="bg-[#26374a] hover:bg-[#1c2b3a] text-white">
              Back to Tests
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results Screen
  if (testCompleted && results) {
    return (
      <div className="min-h-screen bg-white">
        {/* Government Header */}
        <header className="border-b-4 border-[#af3c43] bg-white">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapleLeaf className="w-10 h-10 text-[#af3c43]" />
                <div className="text-sm">
                  <span className="font-semibold">Government</span>
                  <span className="mx-2 text-slate-400">|</span>
                  <span className="font-semibold">Gouvernement</span>
                  <div className="text-xs text-slate-600">of Canada | du Canada</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <Card className={`border-2 ${results.passed ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
            <CardContent className="p-8 text-center">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${results.passed ? 'bg-green-500' : 'bg-red-500'}`}>
                {results.passed ? (
                  <CheckCircle2 className="w-10 h-10 text-white" />
                ) : (
                  <XCircle className="w-10 h-10 text-white" />
                )}
              </div>
              
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {results.passed ? "Test Passed" : "Test Not Passed"}
              </h1>
              <p className="text-lg text-slate-600 mb-6">
                {results.passed 
                  ? "Congratulations! You have successfully passed the citizenship test."
                  : `You need ${passScore}% to pass. Please review the material and try again.`}
              </p>
              
              <div className="grid grid-cols-3 gap-6 max-w-md mx-auto mb-8">
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-4xl font-bold text-[#26374a]">{results.score}%</p>
                  <p className="text-sm text-slate-600">Your Score</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-4xl font-bold text-green-600">{results.correct}</p>
                  <p className="text-sm text-slate-600">Correct</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-4xl font-bold text-slate-600">{results.total - results.correct}</p>
                  <p className="text-sm text-slate-600">Incorrect</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Answer Review */}
          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 border-b-2 border-[#af3c43] pb-2">
              Review Your Answers
            </h2>
            <div className="space-y-4">
              {results.results.map((result, index) => {
                const question = questions.find(q => q.question_id === result.question_id);
                return (
                  <Card key={index} className={`border-l-4 ${result.is_correct ? 'border-l-green-500 bg-green-50' : 'border-l-red-500 bg-red-50'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${result.is_correct ? 'bg-green-500' : 'bg-red-500'}`}>
                          {result.is_correct ? (
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          ) : (
                            <XCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 mb-2">
                            {index + 1}. {question?.question_text}
                          </p>
                          <div className="text-sm space-y-1">
                            <p className={result.is_correct ? 'text-green-700' : 'text-red-700'}>
                              Your answer: {result.user_answer || 'Not answered'} 
                              {question?.options[result.user_answer] && ` - ${question.options[result.user_answer]}`}
                            </p>
                            {!result.is_correct && (
                              <p className="text-green-700 font-medium">
                                Correct answer: {result.correct_answer} - {question?.options[result.correct_answer]}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate("/simulations")} className="border-[#26374a] text-[#26374a]">
              Back to Simulations
            </Button>
            <Button variant="outline" onClick={() => navigate("/mock-tests")} className="border-[#26374a] text-[#26374a]">
              Try Mock Tests
            </Button>
            <Button variant="outline" onClick={() => navigate("/weak-areas")} className="text-orange-600 border-orange-300">
              View Weak Areas
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Test Taking Interface
  return (
    <div className="min-h-screen bg-white" data-testid="real-test-ui">
      {/* Government of Canada Header */}
      <header className="border-b-4 border-[#af3c43] bg-white">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapleLeaf className="w-10 h-10 text-[#af3c43]" />
              <div className="text-sm">
                <span className="font-semibold">Government</span>
                <span className="mx-2 text-slate-400">|</span>
                <span className="font-semibold">Gouvernement</span>
                <div className="text-xs text-slate-600">of Canada | du Canada</div>
              </div>
            </div>
            <div className="text-right text-sm">
              <div>Application number: <span className="font-mono">{applicationNumber}</span></div>
              <div>UCI: <span className="font-mono">{uciNumber}</span></div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-[#26374a] border-b-2 border-[#af3c43] pb-2 inline-block">
            {testTitle}
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Question */}
          <div className="flex-1">
            {/* Question Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#26374a]">
                Question {currentIndex + 1} of {questions.length}
              </h2>
              <div className={`text-xl font-bold ${
                timeLeft < 300 
                  ? 'text-red-600 animate-pulse' 
                  : timeLeft < 600 
                    ? 'text-yellow-600'
                    : 'text-[#26374a]'
              }`}>
                Time left: {formatTime(timeLeft)}
              </div>
            </div>

            {/* Question Text */}
            <div className="mb-6">
              <p className="text-lg text-slate-800">
                {currentQuestion?.question_text}
              </p>
            </div>

            {/* Answer Options */}
            <div className="space-y-3 mb-6">
              {currentQuestion && Object.entries(currentQuestion.options).map(([key, value]) => {
                const isSelected = answers[currentQuestion.question_id] === key;
                return (
                  <label
                    key={key}
                    className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-colors ${
                      isSelected 
                        ? 'bg-blue-50 border border-blue-300' 
                        : 'hover:bg-slate-50 border border-transparent'
                    }`}
                    data-testid={`option-${key}`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion.question_id}`}
                      checked={isSelected}
                      onChange={() => handleAnswerSelect(currentQuestion.question_id, key)}
                      className="mt-1 w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-slate-800">{value}</span>
                  </label>
                );
              })}
            </div>

            {/* Review Later Checkbox */}
            <div className="flex items-center gap-2 mb-6 text-slate-600">
              <Checkbox
                id="review-later"
                checked={reviewLater.has(currentQuestion?.question_id)}
                onCheckedChange={() => toggleReviewLater(currentQuestion?.question_id)}
                className="border-slate-400"
              />
              <label htmlFor="review-later" className="text-sm cursor-pointer">
                I want to review this answer later.
              </label>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="outline"
                onClick={goToPrev}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 border-slate-400 text-slate-700 hover:bg-slate-100"
                data-testid="prev-question-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous question
              </Button>
              <Button
                onClick={goToNext}
                disabled={currentIndex === questions.length - 1}
                className="flex items-center gap-2 bg-[#26374a] hover:bg-[#1c2b3a] text-white"
                data-testid="next-question-btn"
              >
                Next question
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Submit Button */}
            <Button
              onClick={() => setShowSubmitConfirm(true)}
              className="w-full sm:w-auto bg-[#335075] hover:bg-[#26374a] text-white px-8 py-6 text-lg"
              disabled={submitting}
              data-testid="confirm-submission-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                "Confirm submission"
              )}
            </Button>
          </div>

          {/* Right Column - Question Navigator */}
          <div className="lg:w-80">
            <div className="sticky top-4">
              <h3 className="text-lg font-semibold text-[#26374a] mb-3">View questions</h3>
              
              {/* View Toggle */}
              <div className="flex border border-slate-300 rounded-md overflow-hidden mb-4 w-fit">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${
                    viewMode === "grid" 
                      ? 'bg-slate-100 text-slate-900 font-medium' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  Grid view
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-2 px-4 py-2 text-sm border-l border-slate-300 ${
                    viewMode === "list" 
                      ? 'bg-slate-100 text-slate-900 font-medium' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <List className="w-4 h-4" />
                  List view
                </button>
              </div>

              {/* Question Grid/List */}
              {viewMode === "grid" ? (
                <div className="grid grid-cols-5 gap-2 mb-6">
                  {questions.map((q, index) => {
                    const { isAnswered, isReview } = getQuestionStatus(index);
                    const isCurrent = currentIndex === index;
                    
                    return (
                      <button
                        key={q.question_id}
                        onClick={() => goToQuestion(index)}
                        className={`
                          w-10 h-10 rounded border text-sm font-medium transition-all
                          ${isCurrent ? 'ring-2 ring-[#26374a] ring-offset-1' : ''}
                          ${isReview ? 'bg-yellow-400 text-yellow-900 border-yellow-500' : ''}
                          ${isAnswered && !isReview ? 'bg-[#26374a] text-white border-[#26374a]' : ''}
                          ${!isAnswered && !isReview ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200' : ''}
                        `}
                        data-testid={`nav-question-${index + 1}`}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1 mb-6 max-h-64 overflow-y-auto">
                  {questions.map((q, index) => {
                    const { isAnswered, isReview } = getQuestionStatus(index);
                    const isCurrent = currentIndex === index;
                    
                    return (
                      <button
                        key={q.question_id}
                        onClick={() => goToQuestion(index)}
                        className={`
                          w-full text-left px-3 py-2 rounded text-sm transition-all flex items-center justify-between
                          ${isCurrent ? 'ring-2 ring-[#26374a]' : ''}
                          ${isReview ? 'bg-yellow-100 hover:bg-yellow-200' : ''}
                          ${isAnswered && !isReview ? 'bg-blue-50 hover:bg-blue-100' : ''}
                          ${!isAnswered && !isReview ? 'bg-slate-50 hover:bg-slate-100' : ''}
                        `}
                      >
                        <span>Question {index + 1}</span>
                        <span className="text-xs">
                          {isReview ? "Review" : isAnswered ? "Answered" : "Not answered"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Legend */}
              <div className="space-y-2 text-sm border-t pt-4">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-yellow-400 border border-yellow-500"></span>
                  <span className="text-slate-700">To be reviewed: {reviewCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-slate-100 border border-slate-300"></span>
                  <span className="text-slate-700">Not answered: {notAnsweredCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-[#26374a] border border-[#26374a]"></span>
                  <span className="text-slate-700">Answered: {answeredCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSubmitConfirm(false)}>
          <Card className="max-w-md w-full border-2 shadow-xl" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Submit Test?
                </h3>
                <p className="text-slate-600">
                  Are you sure you want to submit your test?
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{answeredCount}</p>
                    <p className="text-xs text-slate-600">Answered</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{reviewCount}</p>
                    <p className="text-xs text-slate-600">To Review</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-400">{notAnsweredCount}</p>
                    <p className="text-xs text-slate-600">Unanswered</p>
                  </div>
                </div>
              </div>

              {notAnsweredCount > 0 && (
                <p className="text-sm text-red-600 mb-4 text-center">
                  Warning: You have {notAnsweredCount} unanswered question(s).
                </p>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSubmitConfirm(false)}
                  className="flex-1"
                >
                  Continue Test
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-[#335075] hover:bg-[#26374a] text-white"
                  data-testid="final-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Test"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RealTestUI;
